'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useCallback, useEffect, useMemo, useSyncExternalStore, } from 'react';
import { SegmentedControl } from '../../forms/segmented-control/index.js';
import { DropdownMenu, DropdownMenuContent, DropdownMenuLabel, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuTrigger, } from '../../overlays/dropdown-menu/index.js';
import { Button } from '../../actions/button/index.js';
import { cx } from '../../../internal/cx.js';
import { applyAppearance } from '@rookcom/monarch-tokens';
import { ONE_YEAR, defaultCookieDomain, documentCookieStore, } from '../../../internal/shell/cookies.js';
import { MoonGlyph, SunGlyph, SystemGlyph } from '../../../internal/shell/icons.js';
/** Root class, declared before any other `mn-` string so dist/manifest.json reads it as the root. */
const ROOT = 'mn-theme-toggle';
/** The cookie every Rook app shares the theme choice in. */
export const ROOK_THEME_COOKIE = 'rook_theme';
/** Fired on `window` after a ThemeToggle writes a new choice (detail: the preference). */
export const ROOK_THEME_EVENT = 'rook-theme';
const PREFERENCES = ['light', 'dark', 'system'];
const isPreference = (v) => typeof v === 'string' && PREFERENCES.includes(v);
const DEFAULT_MESSAGES = {
    label: 'Theme',
    light: 'Light',
    dark: 'Dark',
    system: 'System',
};
/**
 * Applies a preference to `<html>` exactly as the tokens pre-paint script
 * (`appearanceScript` in `@rookcom/monarch-tokens`) does: `system` follows the
 * OS, routes outside `<html data-rook-theme-routes="regex">` stay light, and
 * the contrast attribute is left as it is.
 */
export function applyThemePreference(preference, root = document.documentElement) {
    const prefersDark = typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-color-scheme: dark)').matches;
    let theme = preference === 'system' ? (prefersDark ? 'dark' : 'light') : preference;
    const routes = root.getAttribute('data-rook-theme-routes');
    if (routes) {
        try {
            if (!new RegExp(routes).test(window.location.pathname))
                theme = 'light';
        }
        catch {
            theme = 'light';
        }
    }
    const contrast = root.getAttribute('data-mn-contrast') === 'more' ? 'more' : 'standard';
    applyAppearance(root, { theme, contrast });
    root.style.colorScheme = theme;
    return theme;
}
function subscribe(onChange) {
    window.addEventListener(ROOK_THEME_EVENT, onChange);
    // Another app may have changed the shared cookie while this tab was in the background.
    window.addEventListener('focus', onChange);
    document.addEventListener('visibilitychange', onChange);
    return () => {
        window.removeEventListener(ROOK_THEME_EVENT, onChange);
        window.removeEventListener('focus', onChange);
        document.removeEventListener('visibilitychange', onChange);
    };
}
/** In-memory fallback when `cookies` is null: one value per page, shared by every toggle. */
let memoryPreference = null;
const ICONS = {
    light: _jsx(SunGlyph, {}),
    dark: _jsx(MoonGlyph, {}),
    system: _jsx(SystemGlyph, {}),
};
/**
 * Light, dark or system theme for Rook apps. The choice is saved in the shared
 * `rook_theme` cookie (so every Rook app follows it) and applied to `<html>`
 * the same way the tokens pre-paint script does, so render that script in
 * `<head>` and this toggle anywhere. It renders the default on the server and
 * picks up the saved choice on hydration, so there is no mismatch.
 */
export const ThemeToggle = forwardRef(function ThemeToggle({ value: valueProp, defaultValue = 'light', onValueChange, variant = 'segmented', showLabel = true, side = 'bottom', align = 'start', size = 'md', cookieName = ROOK_THEME_COOKIE, cookieDomain, cookies = documentCookieStore, applyToDocument = true, messages: messagesProp, className, ...rest }, ref) {
    const messages = useMemo(() => ({ ...DEFAULT_MESSAGES, ...messagesProp }), [messagesProp]);
    const read = useCallback(() => {
        const saved = cookies ? cookies.get(cookieName) : memoryPreference;
        return isPreference(saved) ? saved : defaultValue;
    }, [cookies, cookieName, defaultValue]);
    const stored = useSyncExternalStore(subscribe, read, () => defaultValue);
    const value = valueProp ?? stored;
    // `system` follows the OS while the page is open.
    useEffect(() => {
        if (!applyToDocument || value !== 'system' || typeof window.matchMedia !== 'function')
            return;
        const mq = window.matchMedia('(prefers-color-scheme: dark)');
        const onChange = () => applyThemePreference('system');
        mq.addEventListener?.('change', onChange);
        return () => mq.removeEventListener?.('change', onChange);
    }, [applyToDocument, value]);
    const choose = (next) => {
        if (!isPreference(next))
            return;
        if (cookies)
            cookies.set(cookieName, next, {
                maxAge: ONE_YEAR,
                domain: cookieDomain ?? defaultCookieDomain(),
            });
        else
            memoryPreference = next;
        if (applyToDocument)
            applyThemePreference(next);
        window.dispatchEvent(new CustomEvent(ROOK_THEME_EVENT, { detail: next }));
        onValueChange?.(next);
    };
    const options = PREFERENCES.map((p) => ({ value: p, label: messages[p], icon: ICONS[p] }));
    if (variant === 'menu') {
        const current = messages[value];
        return (_jsx("div", { ref: ref, ...rest, className: cx(ROOT, className), "data-variant": "menu", "data-value": value, children: _jsxs(DropdownMenu, { children: [_jsx(DropdownMenuTrigger, { asChild: true, children: _jsx(Button, { variant: "ghost", size: size, className: "mn-theme-toggle__trigger", "aria-label": `${messages.label}: ${current}`, leadingIcon: ICONS[value], children: showLabel ? _jsx("span", { className: "mn-theme-toggle__label", children: current }) : null }) }), _jsxs(DropdownMenuContent, { side: side, align: align, className: "mn-theme-toggle__menu", children: [_jsx(DropdownMenuLabel, { children: messages.label }), _jsx(DropdownMenuRadioGroup, { value: value, onValueChange: choose, children: options.map((o) => (_jsx(DropdownMenuRadioItem, { value: o.value, children: _jsxs("span", { className: "mn-theme-toggle__option", children: [o.icon, o.label] }) }, o.value))) })] })] }) }));
    }
    return (_jsx("div", { ref: ref, ...rest, className: cx(ROOT, className), "data-variant": "segmented", "data-value": value, children: _jsx(SegmentedControl, { "aria-label": messages.label, options: options, value: value, onValueChange: choose, size: size }) }));
});
//# sourceMappingURL=theme-toggle.js.map